import { Grid } from "@mui/material";
import { AnalyticsSpacing } from "consts";
import StandardAnalyticsChartStatCard from "./StandardAnalyticsChartStatCard";
import {
  ApiDimension,
  ApiMetric,
  ApiChartTemplate,
  ApiChartType,
} from "@incendium/api";
import AnalyticsCard from "./AnalyticsCard";
import {
  CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_OVERALL_CONTRIBUTION_MARGIN,
  CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_OVERALL_MER,
  CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_OVERALL_ROI,
  CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_TOTAL_REVENUE,
} from "features/analytics/constants/chartKeys";

function EcomStatsAndFunnel() {
  return (
    <Grid container spacing={AnalyticsSpacing}>
      <Grid item xs={3}>
        <StandardAnalyticsChartStatCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_TOTAL_REVENUE}
          chart={{
            name: "Total Revenue",
            dimension: [ApiDimension.DIMENSION_NOT_SET],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.STAT_CARD,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_REVENUE],
              },
            ],
            displayOptions: {
              statTitle: "Total Revenue",
            },
            comparison: true,
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <StandardAnalyticsChartStatCard
          chartKey={
            CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_OVERALL_CONTRIBUTION_MARGIN
          }
          chart={{
            name: "Overall Contribution Margin",
            dimension: [ApiDimension.DIMENSION_NOT_SET],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.STAT_CARD,
            comparison: true,
            attributes: [],
            includePublisherReadyOnly: true,
            displayOptions: {
              statTitle: "Overall Contribution Margin",
            },
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSION_CONTRIBUTION_MARGIN],
              },
            ],
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <StandardAnalyticsChartStatCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_OVERALL_ROI}
          chart={{
            name: "Overall ROI",
            dimension: [ApiDimension.DIMENSION_NOT_SET],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.STAT_CARD,
            attributes: [],
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_ROI],
              },
            ],
            displayOptions: {
              statTitle: "Overall ROI",
            },
            comparison: true,
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <StandardAnalyticsChartStatCard
          chartKey={CHART_KEY_ECOM_INSIGHTS_OVERVIEW_STAT_OVERALL_MER}
          chart={{
            name: "Overall MER",
            dimension: [ApiDimension.DIMENSION_NOT_SET],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.STAT_CARD,
            attributes: [],
            includePublisherReadyOnly: true,
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSION_EFFICIENCY_RATIO],
              },
            ],
            displayOptions: {
              statTitle: "Overall MER",
            },
            comparison: true,
          }}
        />
      </Grid>
      <Grid item xs={12} sx={{ height: 400 }}>
        <AnalyticsCard
          // funnelShowStageChange
          chart={{
            name: "Ecommerce Conversion Funnel",
            dimension: [],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.FUNNEL,
            comparison: true,
            attributes: [],
            displayOptions: {
              funnelUseLogFn: true,
              fullAttributesList: [ApiDimension.DIMENSION_NOT_SET],
              noAttributionToggle: true,
              metricNames: [
                {
                  metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_FIRED,
                  name: "Sessions with Add to Cart",
                },
                {
                  metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_COUNT,
                  name: "Total Add to Carts",
                },
                {
                  metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                  name: "Add to Cart Rate",
                },
                {
                  metric: ApiMetric.METRIC_SALES_COUNT_LAST,
                  name: "Total Sales",
                },
                {
                  metric: ApiMetric.METRIC_REVENUE_LAST,
                  name: "Total Revenue",
                },
                {
                  metric: ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST,
                  name: "Total New Customers",
                },
                {
                  metric: ApiMetric.METRIC_AVERAGE_SALE_VALUE_LAST,
                  name: "AOV",
                },
                {
                  metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                  name: "Total Product Pageviews",
                },
                {
                  metric: ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED,
                  name: "Sessions where PDP viewed",
                },
                {
                  metric: ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED_RATE,
                  name: "PDP viewed rate",
                },
                {
                  metric: ApiMetric.METRIC_CHECKOUT_START_COUNT,
                  name: "Sessions with Checkout Start",
                },
              ],
              funnelAdditonalMetrics: [
                {
                  key: ApiMetric.METRIC_SESSIONS_COUNT,
                  metrics: [
                    ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                    ApiMetric.METRIC_PAID_SESSION_COUNT,
                    ApiMetric.METRIC_ORGANIC_SESSION_COUNT,
                  ],
                },
                {
                  key: ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED,
                  metrics: [
                    ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                    ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED_RATE,
                    ApiMetric.METRIC_PRODUCT_PAGE_EFFECTIVENESS_RATE,
                    ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                  ],
                },
                {
                  key: ApiMetric.METRIC_SESSION_ADD_TO_CART_FIRED,
                  metrics: [
                    ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_COUNT,
                    ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                    ApiMetric.METRIC_CART_ABANDONMENT_RATE,
                  ],
                },
                {
                  key: ApiMetric.METRIC_CHECKOUT_START_COUNT,
                  metrics: [
                    ApiMetric.METRIC_CHECKOUT_START_RATE,
                    ApiMetric.METRIC_CHECKOUT_ABANDONMENT_RATE,
                  ],
                },
                {
                  key: ApiMetric.METRIC_SALES_COUNT_LAST,
                  metrics: [
                    ApiMetric.METRIC_REVENUE_LAST,
                    ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST,
                    ApiMetric.METRIC_AVERAGE_SALE_VALUE_LAST,
                  ],
                },
              ],
            },
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_PAID_SESSION_COUNT,
                  ApiMetric.METRIC_ORGANIC_SESSION_COUNT,
                  ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED,
                  ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED_RATE,
                  ApiMetric.METRIC_PRODUCT_PAGE_EFFECTIVENESS_RATE,
                  ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                  ApiMetric.METRIC_SESSION_ADD_TO_CART_FIRED,
                  ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_COUNT,
                  ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                  ApiMetric.METRIC_CART_ABANDONMENT_RATE,
                  ApiMetric.METRIC_CHECKOUT_START_COUNT,
                  ApiMetric.METRIC_CHECKOUT_START_RATE,
                  ApiMetric.METRIC_CHECKOUT_ABANDONMENT_RATE,
                  ApiMetric.METRIC_SALES_COUNT_LAST,
                  ApiMetric.METRIC_REVENUE_LAST,
                  ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST,
                  ApiMetric.METRIC_AVERAGE_SALE_VALUE_LAST,
                ],
              },
            ],
          }}
        />
      </Grid>
    </Grid>
  );
}

export default EcomStatsAndFunnel;
